import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Map,
  MapPin,
  Globe,
  DollarSign,
  Building,
  ChevronRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const goldIcon = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="width: 24px; height: 24px; background: #c9a227; border-radius: 50%; border: 3px solid white; box-shadow: 0 0 15px rgba(201, 162, 39, 0.6);"></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12]
});

const RegionsPage = () => {
  const [regions, setRegions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [regionsRes, projectsRes] = await Promise.all([
          axios.get(`${API}/regions`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setRegions(regionsRes.data.regions);
        setProjects(projectsRes.data.projects);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const formatCurrency = (val) => {
    if (val >= 1000000000) return `$${(val / 1000000000).toFixed(2)}B`;
    if (val >= 1000000) return `$${(val / 1000000).toFixed(0)}M`;
    return `$${val?.toLocaleString() || 0}`;
  };

  const getRegionStats = (regionName) => {
    const regionProjects = projects.filter(p => p.region === regionName);
    return {
      count: regionProjects.length,
      totalInvestment: regionProjects.reduce((sum, p) => sum + (p.budget_total || 0), 0),
      projects: regionProjects
    };
  };

  const filteredProjects = selectedRegion 
    ? projects.filter(p => p.region === selectedRegion) 
    : projects;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-[#c9a227] text-xl">Loading regions...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="regions-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
          African Regions
        </h1>
        <p className="text-slate-400 mt-1">16 strategic regions across the African continent</p>
      </div>

      {/* Map & Regions Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map */}
        <div className="lg:col-span-2 h-[500px] rounded-sm overflow-hidden glass-card">
          <MapContainer
            center={[5, 20]}
            zoom={3}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
              url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            />
            {/* Region Centers */}
            {regions.map((region) => {
              const stats = getRegionStats(region.name);
              return region.lat && region.lng && (
                <CircleMarker
                  key={region.id}
                  center={[region.lat, region.lng]}
                  radius={10 + stats.count * 3}
                  pathOptions={{
                    fillColor: selectedRegion === region.name ? '#c9a227' : '#3b82f6',
                    fillOpacity: 0.6,
                    color: selectedRegion === region.name ? '#c9a227' : '#3b82f6',
                    weight: 2
                  }}
                  eventHandlers={{
                    click: () => setSelectedRegion(selectedRegion === region.name ? null : region.name)
                  }}
                >
                  <Popup>
                    <div className="text-white min-w-[180px]">
                      <h3 className="font-semibold text-[#c9a227]">{region.name}</h3>
                      <p className="text-xs text-slate-400 mt-1">{region.code} - {stats.count} Projects</p>
                      <p className="text-sm text-[#c9a227] font-semibold">{formatCurrency(stats.totalInvestment)}</p>
                      <p className="text-xs text-slate-500 mt-1">Countries: {region.countries_list?.slice(0, 3).join(', ')}</p>
                    </div>
                  </Popup>
                </CircleMarker>
              );
            })}
            {/* Project Markers */}
            {filteredProjects.map((project) => project.lat && project.lng && (
              <Marker
                key={project.id}
                position={[project.lat, project.lng]}
                icon={goldIcon}
                eventHandlers={{
                  click: () => navigate(`/projects/${project.id}`)
                }}
              >
                <Popup>
                  <div className="text-white min-w-[200px]">
                    <h3 className="font-semibold text-[#c9a227]">{project.name}</h3>
                    <p className="text-sm text-slate-400">{project.country}</p>
                    <p className="text-sm mt-1">Budget: <span className="text-[#c9a227]">{formatCurrency(project.budget_total)}</span></p>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        {/* Regions List */}
        <Card className="glass-card max-h-[500px] overflow-y-auto">
          <CardHeader>
            <CardTitle className="text-slate-200 flex items-center gap-2">
              <Globe className="w-5 h-5 text-[#c9a227]" />
              Regions ({regions.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <button
              onClick={() => setSelectedRegion(null)}
              className={`w-full p-3 rounded-sm text-left transition-colors ${
                !selectedRegion ? 'bg-[#c9a227]/20 border border-[#c9a227]/50' : 'bg-slate-800/30 hover:bg-slate-800/50'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className={!selectedRegion ? 'text-[#c9a227]' : 'text-white'}>All Regions</span>
                <Badge className="bg-[#c9a227]/20 text-[#c9a227]">{projects.length}</Badge>
              </div>
            </button>
            {regions.map((region) => {
              const stats = getRegionStats(region.name);
              const isSelected = selectedRegion === region.name;
              return (
                <button
                  key={region.id}
                  onClick={() => setSelectedRegion(isSelected ? null : region.name)}
                  className={`w-full p-3 rounded-sm text-left transition-colors ${
                    isSelected ? 'bg-[#c9a227]/20 border border-[#c9a227]/50' : 'bg-slate-800/30 hover:bg-slate-800/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={isSelected ? 'text-[#c9a227] font-medium' : 'text-white'}>{region.name}</p>
                      <p className="text-xs text-slate-500">{region.countries_list?.slice(0, 3).join(', ')}</p>
                    </div>
                    <div className="text-right">
                      <Badge className={stats.count > 0 ? 'bg-[#c9a227]/20 text-[#c9a227]' : 'bg-slate-700 text-slate-400'}>
                        {stats.count}
                      </Badge>
                      {stats.totalInvestment > 0 && (
                        <p className="text-xs text-slate-500 mt-1">{formatCurrency(stats.totalInvestment)}</p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* Projects in Selected Region */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="text-slate-200">
            {selectedRegion ? `Projects in ${selectedRegion}` : 'All Projects'} ({filteredProjects.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredProjects.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredProjects.map((project) => (
                <div
                  key={project.id}
                  onClick={() => navigate(`/projects/${project.id}`)}
                  className="p-4 bg-slate-800/30 rounded-sm cursor-pointer hover:bg-slate-800/50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-[#c9a227]" />
                      <div>
                        <p className="font-medium text-white">{project.name?.length > 30 ? project.name.slice(0, 30) + '...' : project.name}</p>
                        <p className="text-xs text-slate-500">{project.country}</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-500" />
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
                    <div>
                      <p className="text-slate-500">Budget</p>
                      <p className="text-[#c9a227] font-semibold">{formatCurrency(project.budget_total)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Completion</p>
                      <p className="text-green-400 font-semibold">{project.completion_percentage || 0}%</p>
                    </div>
                  </div>
                  <Badge className="mt-2 status-green">{project.industry || project.sector}</Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <Building className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No projects in this region</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RegionsPage;
